import {
  Hint,
  fixedForwardRef,
  useCurrentValueGetter,
  usePlatformInfo,
} from '@acsbe/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { fromPairs, mapValues } from 'lodash-es';
import {
  ComponentPropsWithoutRef,
  ForwardedRef,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FetcherWithComponents,
  Form,
  useActionData,
  useFetcher,
  useNavigation,
} from 'react-router-dom';
import { z } from 'zod';

export class FormFieldValidationError extends Error {
  public field?: string;
  constructor(message: string, field?: string) {
    super(message);
    this.field = field;
  }
}

export class RedirectRequiredError extends Error {
  public url: string;
  constructor(url: string) {
    super('Redirect required');
    this.url = url;
  }
}

export type Entry<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T];

export function parseForm<Model>(formData: FormData): Model {
  const entries = Array.from(formData as unknown as Iterable<Entry<Model>>);
  return fromPairs(entries) as Model;
}

export type ValidationResult<T> = Partial<{
  [k in keyof T]: {
    _errors: string[];
  };
}>;

type BasicFormActionResult<Schema> =
  | ValidationResult<Schema>
  | undefined
  | string
  | boolean;

export type BasicFormResult<Schema> = Promise<
  Exclude<BasicFormActionResult<Schema>, undefined>
>;

export function simpleError<T>(
  field: keyof T,
  ...errors: string[]
): ValidationResult<T> {
  const result: ValidationResult<T> = {};
  result[field] = {
    _errors: errors,
  };
  return result;
}

export function simpleErrors<T>(
  errors: Record<keyof T, string>,
): ValidationResult<T> {
  return mapValues(errors, (error) => ({
    _errors: [error],
  }));
}

export function hasIssues<T>(
  issues: ValidationResult<T>,
  key: keyof T,
): boolean {
  const count = issues[key]?._errors?.length ?? 0;

  return count > 0;
}

interface ValidationsOptions {
  clearOnInput?: boolean;
  clearOnFocus?: boolean;
  clearOnBlur?: boolean;
  clearOnChange?: boolean;
  fetcher?: FetcherWithComponents<unknown>;
}

export function useValidationResult<T>({
  clearOnBlur = false,
  clearOnFocus = false,
  clearOnInput = false,
  clearOnChange = false,
  fetcher,
}: ValidationsOptions = {}) {
  const formRef = useRef<HTMLFormElement>(null);
  const actualIssuesFromRoute = useActionData() as ValidationResult<T>;
  const actualIssuesFromFetcher = fetcher?.data as
    | ValidationResult<T>
    | undefined;
  const actualIssues = fetcher
    ? actualIssuesFromFetcher
    : actualIssuesFromRoute;
  const [hideIssuesFor, setHideIssuesFor] = useState<Set<string>>(
    () => new Set(),
  );
  const issues: ValidationResult<T> = useMemo(() => {
    if (!actualIssues) return {};
    return Object.fromEntries(
      Object.entries(actualIssues).filter(([key]) => !hideIssuesFor.has(key)),
    ) as ValidationResult<T>;
  }, [actualIssues, hideIssuesFor]);

  const { isJSDOM } = usePlatformInfo();

  useEffect(() => {
    const { current } = formRef;
    if (!current) return;

    async function handleEvent(event: Event) {
      const { name } = event.target as unknown as HTMLInputElement;
      if (!name) return;
      if (!isJSDOM) {
        const abortController = new AbortController();
        await Promise.race([
          new Promise((resolve) => setTimeout(resolve, 500)),
          new Promise<void>((resolve) => {
            current?.addEventListener(
              'submit',
              () => {
                resolve();
                abortController.abort();
              },
              { signal: abortController.signal },
            );
          }),
        ]);
      }

      setHideIssuesFor((prev) => {
        const next = new Set(prev);
        next.add(name);
        return next;
      });
    }

    const abortController = new AbortController();

    if (clearOnBlur) {
      current.addEventListener('focusout', handleEvent, {
        signal: abortController.signal,
      });
    }
    if (clearOnFocus) {
      current.addEventListener('focusin', handleEvent, {
        signal: abortController.signal,
      });
    }
    if (clearOnInput) {
      current.addEventListener('input', handleEvent, {
        signal: abortController.signal,
      });
    }

    if (clearOnChange) {
      current.addEventListener('change', handleEvent, {
        signal: abortController.signal,
      });
    }

    return () => {
      abortController.abort();
    };
  }, [clearOnBlur, clearOnFocus, clearOnInput, clearOnChange, isJSDOM]);

  useEffect(() => {
    setHideIssuesFor(new Set());
  }, [actualIssues]);

  useEffect(() => {
    if (!actualIssues) return;
    const selector = Object.keys(actualIssues)
      .map((name) => `[name="${name}"]`)
      .join(',');
    if (!selector) return;
    const element = document.querySelector<HTMLInputElement>(selector);
    element?.focus();
  }, [actualIssues]);

  return {
    formRef,
    issues,
  };
}

type Translator = ReturnType<typeof useTranslation<'translation'>>['t'];

type FieldOptions = {
  values?: Parameters<Translator>[1] & object;
  required?: boolean;
};

export function fieldProps<T>(
  field: keyof T & string,
  issues: ValidationResult<T>,
  translator: Translator,
  { required = true, values }: FieldOptions = {},
) {
  return {
    name: field,
    required: required,
    'aria-required': required,
    'aria-invalid': hasIssues(issues, field),
    description: issues?.[field]?._errors.map((e) => translator(e, values))[0],
  };
}

export function serverErrorValidation<T>(
  issues: string,
  translator: Translator,
) {
  return {
    'aria-invalid': true,
    description: translator(issues),
  };
}

export function useIsSubmitting(fetcher?: FetcherWithComponents<unknown>) {
  const { state } = useNavigation();

  if (fetcher) {
    return fetcher.state === 'submitting';
  }

  return state === 'submitting';
}

export function useGetFormError<Schema extends z.Schema>(
  formState: ReturnType<typeof useForm<z.infer<Schema>>>['formState'],
) {
  const { t } = useTranslation();
  return (name: keyof z.infer<Schema>) => {
    const error = (
      formState.errors as FieldErrors<Record<typeof name, unknown>>
    )[name];
    const message = t((error?.message as string) ?? 'FIELD_ID_REQUIRED');

    return error && <Hint state="error">{message}</Hint>;
  };
}

export function useZodForm<Schema extends z.Schema>(
  schema: Schema,
  options: Parameters<typeof useForm<z.infer<Schema>>>[0] = {},
) {
  const { t } = useTranslation();
  const form = useForm<z.infer<Schema>>({
    resolver: zodResolver(schema),
    ...options,
  });
  const getFormError = useGetFormError(form.formState);

  const hookup = (
    name: Parameters<typeof form.register>[0],
    applyLabelPrefix?: string,
  ) => {
    const error = getFormError(name);
    const props = {
      ...form.register(name),
      ...(applyLabelPrefix
        ? {
            label: t(applyLabelPrefix + name),
          }
        : {}),
      'aria-invalid': !!error,
    };

    if (!error) return props;

    return {
      ...props,
      description: error,
    };
  };

  return { ...form, hookup };
}

function useField<Schema>(issues: ValidationResult<Schema>) {
  const { t } = useTranslation();
  const field = useCallback(
    (name: keyof Schema & string, options?: FieldOptions) => {
      return fieldProps(name, issues, t, options);
    },
    [issues, t],
  );

  return field;
}

export interface IBasicFormContext<Schema> {
  field: ReturnType<typeof useField<Schema>>;
  submissionResult: BasicFormActionResult<Schema>;
  isSubmitting: boolean;
  submit: () => void;
}

//IMPORTANT: This any is here for a good reason
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BasicFormContext = createContext<IBasicFormContext<any> | null>(null);

export function useBasicForm<Schema>() {
  const context = useContext(BasicFormContext);

  if (!context) {
    throw new Error('useBasicForm must be used within a BasicForm');
  }

  return context as IBasicFormContext<Schema>;
}

function ForwardedBasicForm<Schema>(
  {
    children,
    onGeneralError,
    onSuccess,
    onUpdate,
    ...props
  }: Omit<ComponentPropsWithoutRef<typeof Form>, 'children'> & {
    children: (_: IBasicFormContext<Schema>) => ReactNode;
    onSuccess?: () => void;
    onGeneralError?: (error?: string) => void;
    onUpdate?: () => void;
  },
  ref: ForwardedRef<IBasicFormContext<Schema>>,
) {
  const fetcher = useFetcher();
  const isSubmitting = useIsSubmitting(fetcher);
  const { issues, formRef } = useValidationResult<Schema>({
    clearOnChange: true,
    fetcher,
  });
  const field = useField<Schema>(issues);
  const submissionResult = fetcher.data as BasicFormActionResult<Schema>;
  const callbacks = useCurrentValueGetter({
    onSuccess,
    onGeneralError,
    onUpdate,
  });
  useEffect(() => {
    const { onSuccess, onGeneralError, onUpdate } = callbacks();
    if (submissionResult === true) onSuccess?.();
    if (submissionResult === false) onGeneralError?.();
    if (typeof submissionResult === 'string')
      onGeneralError?.(submissionResult);
    onUpdate?.();
  }, [submissionResult, callbacks, isSubmitting]);
  const ctx: IBasicFormContext<Schema> = {
    field,
    submissionResult,
    isSubmitting,
    submit: () => fetcher.submit(formRef.current!),
  };

  useImperativeHandle(ref, () => ctx);

  return (
    <fetcher.Form ref={formRef} noValidate aria-busy={isSubmitting} {...props}>
      <BasicFormContext.Provider value={ctx}>
        {children(ctx)}
      </BasicFormContext.Provider>
    </fetcher.Form>
  );
}

export const BasicForm = fixedForwardRef(ForwardedBasicForm);
